import { NoCrash } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { ExplanationAccordion, LabelField, NumericTextField } from 'components'
import { Chip } from 'components/chips'
import * as controllers from 'controllers'
import { CompanyDto, VehicleDto, VehicleTypeDto, vehicleValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { KG, LB } from 'localConstants'
import { useEffect, useId, useState } from 'react'

interface AddVINDialogProps {
  initialValues: VehicleDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: VehicleDto) => void
}

export default function AddVINDialog({ initialValues, isLoading, onClose, onSave, open }: AddVINDialogProps) {
  const [isCheckingVIN, setIsCheckingVIN] = useState<boolean>(false)
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeDto[]>([])
  const [exporters, setExporters] = useState<CompanyDto[]>([])
  const weightGVWRUnitLabelId = useId()
  const weightGVWRUnitHelperTextId = useId()

  useEffect(() => {
    controllers
      .getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isExporterCompany'] })
      .then(response => {
        setExporters(response.value)
      })

    controllers.getVehicleTypes().then(response => {
      setVehicleTypes(response.value)
    })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={vehicleValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setValues, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={0} alignItems='center'>
                <Grid item>
                  <DialogTitle sx={{ pr: 1 }}>Add VIN</DialogTitle>
                </Grid>

                <Grid item>{values.vehicleGuid && values.isLocked && <Chip variant='red'>VEHICLE LOCKED</Chip>}</Grid>
              </Grid>

              <DialogContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <Typography fontWeight='bold' variant='body1'>
                              Overview
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              disableListWrap
                              getOptionLabel={option => option.name!}
                              onBlur={handleBlur}
                              onChange={(_e, value) => {
                                setFieldValue('exporterCompany', value)
                              }}
                              options={exporters.map(exporterCompany => ({
                                companyGuid: exporterCompany.companyGuid,
                                name: exporterCompany.name
                              }))}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  error={Boolean(touched.exporterCompany && errors.exporterCompany)}
                                  helperText={touched.exporterCompany && errors.exporterCompany}
                                  label='Exporter'
                                  name='exporter'
                                />
                              )}
                              value={
                                values.exporterCompany
                                  ? { companyGuid: values.exporterCompany.companyGuid, name: values.exporterCompany.name }
                                  : null
                              }
                            />
                          </Grid>

                          <Grid item xs={12} sm={true}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.vin && errors.vin)}
                              fullWidth
                              helperText={touched.vin && errors.vin}
                              label='VIN'
                              name='vin'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.vin}
                            />
                          </Grid>

                          <Grid item xs={12} sm='auto'>
                            <LoadingButton
                              disabled={
                                !values.vin ||
                                values.vin.length !== 17 ||
                                isCheckingVIN ||
                                isLoading ||
                                isSubmitting ||
                                initialValues.isLocked
                              }
                              color='primary'
                              endIcon={<NoCrash />}
                              loading={isCheckingVIN}
                              loadingIndicator='DECODING...'
                              onClick={() => {
                                if (values.vin) {
                                  setIsCheckingVIN(true)
                                  controllers
                                    .decodeVinValues(values.vin, values.year?.toString())
                                    .then(response => {
                                      setValues({
                                        ...values,
                                        year: response.value?.year,
                                        make: response.value?.make,
                                        model: response.value?.model,
                                        oem: response.value?.oem,
                                        mfgCity: response.value?.mfgCity,
                                        mfgState: response.value?.mfgState,
                                        mfgCountry: response.value?.mfgCountry
                                      })
                                    })
                                    .finally(() => {
                                      setIsCheckingVIN(false)
                                    })
                                }
                              }}
                              variant='text'
                            >
                              CHECK VIN
                            </LoadingButton>
                          </Grid>

                          {/* <Grid item xs={12}>
                            <TextField label='Last 6 of VIN' value={values.vin ? values.vin.slice(-6) : ''} />
                          </Grid> */}

                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              disableListWrap
                              getOptionLabel={option => option.name!}
                              onBlur={handleBlur}
                              onChange={(_e, value) => {
                                setFieldValue('vehicleType', value)
                              }}
                              options={vehicleTypes.map(vehicleType => ({
                                vehicleTypeGuid: vehicleType.vehicleTypeGuid,
                                name: vehicleType.name,
                                specialIdentifier: vehicleType.specialIdentifier
                              }))}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  error={Boolean(touched.vehicleType && errors.vehicleType)}
                                  helperText={touched.vehicleType && errors.vehicleType}
                                  label='Vehicle Type'
                                  name='vehicleType'
                                />
                              )}
                              value={
                                values.vehicleType
                                  ? {
                                      vehicleTypeGuid: values.vehicleType.vehicleTypeGuid,
                                      name: values.vehicleType.name,
                                      specialIdentifier: values.vehicleType.specialIdentifier
                                    }
                                  : null
                              }
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.year && errors.year)}
                              helperText={touched.year && errors.year}
                              label='Year'
                              name='year'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.year || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.make && errors.make)}
                              helperText={touched.make && errors.make}
                              label='Make'
                              name='make'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.make || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.model && errors.model)}
                              helperText={touched.model && errors.model}
                              label='Model '
                              name='model'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.model || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.oem && errors.oem)}
                              helperText={touched.oem && errors.oem}
                              label='OEM'
                              name='oem'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.oem || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.mfgBuildDateMonth && errors.mfgBuildDateMonth)}
                              helperText={touched.mfgBuildDateMonth && errors.mfgBuildDateMonth}
                              label='MFG Build Date (Month)'
                              name='mfgBuildDateMonth'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.mfgBuildDateMonth || ''}
                            >
                              <MenuItem value={1}>1</MenuItem>
                              <MenuItem value={2}>2</MenuItem>
                              <MenuItem value={3}>3</MenuItem>
                              <MenuItem value={4}>4</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={6}>6</MenuItem>
                              <MenuItem value={7}>7</MenuItem>
                              <MenuItem value={8}>8</MenuItem>
                              <MenuItem value={9}>9</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={11}>11</MenuItem>
                              <MenuItem value={12}>12</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.mfgBuildDateYear && errors.mfgBuildDateYear)}
                              helperText={touched.mfgBuildDateYear && errors.mfgBuildDateYear}
                              label='MFG Build Date (Year)'
                              name='mfgBuildDateYear'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.mfgBuildDateYear || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.mfgCity && errors.mfgCity)}
                              helperText={touched.mfgCity && errors.mfgCity}
                              label='MFG City'
                              name='mfgCity'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.mfgCity || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.mfgState && errors.mfgState)}
                              helperText={touched.mfgState && errors.mfgState}
                              label='MFG State'
                              name='mfgState'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.mfgState || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.mfgCountry && errors.mfgCountry)}
                              helperText={touched.mfgCountry && errors.mfgCountry}
                              label='MFG Country'
                              name='mfgCountry'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.mfgCountry || ''}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || isCheckingVIN || initialValues.isLocked}
                              error={Boolean(touched.amountUSD && errors.amountUSD)}
                              helperText={touched.amountUSD && errors.amountUSD}
                              InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                              label='Amount (USD)'
                              name='amountUSD'
                              onBlur={handleBlur}
                              onChange={e => {
                                // With `onChange={handleChange}`, if the user entered an amountUSD and then cleared the field,
                                // the backend/API would throw the error:
                                // """
                                // "$.amountUSD": [
                                //   "The JSON value could not be converted to System.Nullable`1[System.Decimal]. Path: $.amountUSD | LineNumber: 0 | BytePositionInLine: 471."
                                // ]
                                // """
                                // Maxwell Sommer 12/12/2024
                                setFieldValue('amountUSD', e.target.value || undefined)
                              }}
                              value={values.amountUSD}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='VSA Code' value={values.vsaCode} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Importer Stock #' value={values.importerStockNumber} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField
                              label='Vehicle is 25 or more years old'
                              value={values.isVehicle25OrMoreYearsOld ? 'Yes' : 'No'}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Appendix B Vehicle' value={values.isAppendixBVehicle ? 'Yes' : 'No'} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Country of Origin (C/O)' value={values.countryOfOrigin} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <Typography fontWeight='bold' variant='body1'>
                              Weight
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.weightGVWR && errors.weightGVWR)}
                              helperText={touched.weightGVWR && errors.weightGVWR}
                              label='GVWR'
                              name='weightGVWR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.weightGVWR ?? ''}
                            />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <FormControl
                              component='fieldset'
                              error={Boolean(
                                touched.weightGVWRUnitOfMeasurement && errors.weightGVWRUnitOfMeasurement
                              )}
                              required
                              variant='standard'
                            >
                              <FormLabel component='legend' id={weightGVWRUnitLabelId}>
                                GVWR Unit of Measurement
                              </FormLabel>
                              <RadioGroup
                                aria-describedby={
                                  touched.weightGVWRUnitOfMeasurement && errors.weightGVWRUnitOfMeasurement
                                    ? weightGVWRUnitHelperTextId
                                    : undefined
                                }
                                aria-labelledby={weightGVWRUnitLabelId}
                                name='weightGVWRUnitOfMeasurement'
                                onBlur={handleBlur}
                                onChange={handleChange}
                                row
                                value={values.weightGVWRUnitOfMeasurement ?? ''}
                              >
                                <FormControlLabel
                                  value={KG}
                                  control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                  label='Kg'
                                />
                                <FormControlLabel
                                  value={LB}
                                  control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                  label='Lb'
                                />
                              </RadioGroup>
                              {touched.weightGVWRUnitOfMeasurement && errors.weightGVWRUnitOfMeasurement && (
                                <FormHelperText id={weightGVWRUnitHelperTextId}>
                                  {errors.weightGVWRUnitOfMeasurement}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GVWR (Kg)' value={values.weightGVWRKg} />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GVWR (Lbs)' value={values.weightGVWRLbs} />
                          </Grid>

                          <Grid item xs={12}>
                            <LabelField label='Curb Weight' value={values.curbWeight} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.weightGAWRFR && errors.weightGAWRFR)}
                              helperText={touched.weightGAWRFR && errors.weightGAWRFR}
                              label='GAWR FR'
                              name='weightGAWRFR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.weightGAWRFR}
                            />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <RadioGroup
                              name='weightGAWRFRUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.weightGAWRFRUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='KG'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='Kg'
                              />

                              <FormControlLabel
                                value='LB'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='Lb'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GAWR FR (Kg)' value={values.weightGAWRFRKg} />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GAWR FR (Lbs)' value={values.weightGAWRFRLbs} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.weightGAWRRR && errors.weightGAWRRR)}
                              helperText={touched.weightGAWRRR && errors.weightGAWRRR}
                              label='GAWR RR'
                              name='weightGAWRRR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.weightGAWRRR}
                            />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <RadioGroup
                              name='weightGAWRRRUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.weightGAWRRRUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='KG'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='Kg'
                              />

                              <FormControlLabel
                                value='LB'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='Lb'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GAWR RR (Kg)' value={values.weightGAWRRRKg} />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='GAWR RR (Lbs)' value={values.weightGAWRRRLbs} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <Typography fontWeight='bold' variant='body1'>
                              Tires
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.frontTires && errors.frontTires)}
                              helperText={touched.frontTires && errors.frontTires}
                              label='Front Tires'
                              name='frontTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.frontTires}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.frontRims && errors.frontRims)}
                              helperText={touched.frontRims && errors.frontRims}
                              label='Front Rims'
                              name='frontRims'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.frontRims}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.frontTiresPressure && errors.frontTiresPressure)}
                              helperText={touched.frontTiresPressure && errors.frontTiresPressure}
                              label='Front Tires PSI'
                              name='frontTiresPressure'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.frontTiresPressure}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='frontTiresPressureUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.frontTiresPressureUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='kPa'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='kPa'
                              />
                              <FormControlLabel
                                value='PSI'
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='PSI'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='Front Tires (kPa)' value={values.frontTiresPressurekPa} />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='Front Tires (PSI)' value={values.frontTiresPressurePSI} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.rearTires && errors.rearTires)}
                              helperText={touched.rearTires && errors.rearTires}
                              label='Rear Tires'
                              name='rearTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.rearTires}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.rearRims && errors.rearRims)}
                              helperText={touched.rearRims && errors.rearRims}
                              label='Rear Rims'
                              name='rearRims'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.rearRims}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <NumericTextField
                              allowDecimal
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.rearTiresPressure && errors.rearTiresPressure)}
                              helperText={touched.rearTiresPressure && errors.rearTiresPressure}
                              label='Rear Tires PSI'
                              name='rearTiresPressure'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.rearTiresPressure}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='rearTiresPressureUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.rearTiresPressureUnitOfMeasurement}
                            >
                              <FormControlLabel
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='kPa'
                                value='kPa'
                              />
                              <FormControlLabel
                                control={<Radio disabled={isLoading || isSubmitting || initialValues.isLocked} />}
                                label='PSI'
                                value='PSI'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='Rear Tires (kPa)' value={values.rearTiresPressurekPa} />
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <LabelField label='Rear Tires (PSI)' value={values.rearTiresPressurePSI} />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              disabled={isLoading || isSubmitting || initialValues.isLocked}
                              error={Boolean(touched.numberOfRearTires && errors.numberOfRearTires)}
                              helperText={touched.numberOfRearTires && errors.numberOfRearTires}
                              label='Number Of Rear Tires'
                              name='numberOfRearTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              select
                              value={values.numberOfRearTires}
                            >
                              <MenuItem value={2}>2</MenuItem>
                              <MenuItem value={4}>4</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Rear Tire Config' value={values.rearTireConfig} />
                          </Grid>

                          <Grid item xs={12}>
                            <ExplanationAccordion variant='outlined'>
                              {`|| Importer users must select an Exporter company from the Exporter dropdown and then add the vehicle’s details. 
                                || To save a VIN, these fields are required: Exporter, VIN, Vehicle Type, Year, Make, Model, OEM, MFG Build Date (Month), MFG Build Date (Year), GVWR, and GVWR Unit of Measurement (Kg. or Lb.).
                                || The “Vehicle Type” dropdown contains options Car, Oversize Vehicle, SUV, and Truck. 
                                || When saving, the VIN is compared against Appendix A to confirm it can be imported. If it cannot be imported, a warning will be displayed saying, "This vehicle cannot be imported per NHTSA TPMS Appendix A (Canadian-Certified Vehicles Not Originally Manufactured with FMVSS No. 138-Compliant TPMS)". 
                                || When saving, the VIN is checked against all other VINs already in VTS2 and if the VIN already exists, a warning will be displayed saying, “This VIN already exists in VTS2.” 
                                || VSA Code is populated when the vehicle record is created. 
                                || - When the Vehicle Type is Car, the VSA Code will be “VSA-80”. 
                                || - When the Vehicle Type is not Car and the GVWR is 4,536 Kg (10,000 Lb.) or less, the VSA Code will be “VSA-81”. 
                                || - When the Vehicle Type is not Car and the GVWR is greater than 4,536 Kg (10,000 Lb.), the VSA Code will be “VSA-82”. 
                                || The Importer Stock# is a label that is a sequential number beginning with 20000 followed by the 3 character “Exporter Code” or 3 character “Buyer Code” if there is a buyer on the vehicle (e.g., 20001-ABC). This field is only populated when the vehicle is locked. 
                                || “Vehicle is 25 or more years old” = Yes if {This month}/1/{This year} - {MFG Build Date (Month)}/1/{MFG Build Date (Year)} >= 25 
                                || “Appendix B Vehicle” = Yes if the vehicle’s Year/Make/Model combination appears in the Appendix B list or if {MFG Build Date (Month)}/1/{MFG Build Date (Year)} is on or after 9/1/2006. (This is September 1st, 2006). 
                                || “Country of Origin (C/O)” is automatically selected from the MFG Country when the vehicle record is saved. It remains blank when the MFG Country does not match a supported country.
                                || When the vehicle record is saved, the value entered in the GVWR field is saved in VTS2 in both metric and imperial and is used to populate the labels “GVWR (Kg.)”, “GVWR (Lbs.)”, and “Curb Weight”. If the GVWR value is entered with the metric unit of measure (Kg.), the conversion to imperial (Lbs.) uses the formula 1 Kg. = 2.205 Lb. and if the GVWR value is entered with the imperial unit of measure (Lbs.), the conversion to metric (Kg.) uses the formula 2.205 Lbs. = 1 Kg. 
                                || Curb Weight uses the GVWR in Lbs. value. Note that Curb Weight and Gross Vehicle Weight Ratings (GVWR) differ outside of VTS2. While curb weight is the vehicle's standalone weight, GVWR is the maximum weight a vehicle is rated to hold safely, including its own weight, plus passengers and cargo. 
                                || When the vehicle record is saved, the value entered in the GAWR FR field is saved in VTS2 in both metric and imperial and is used to populate the labels “GAWR FR (Kg.)”, “GAWR FR (Lbs.)”. If the GAWR FR value is entered with the metric unit of measure (Kg.), the conversion to imperial (Lbs.) uses the formula 1 Kg. = 2.205 Lb. and if the GAWR FR value is entered with the imperial unit of measure (Lbs.), the conversion to metric (Kg.) uses the formula 2.205 Lbs. = 1 Kg. 
                                || When the vehicle record is saved, the value entered in the GAWR RR field is saved in VTS2 in both metric and imperial and is used to populate the labels “GAWR RR (Kg.)”, “GAWR RR (Lbs.)”. If the GAWR RR value is entered with the metric unit of measure (Kg.), the conversion to imperial (Lbs.) uses the formula 1 Kg. = 2.205 Lb. and if the GAWR RR value is entered with the imperial unit of measure (Lbs.), the conversion to metric (Kg.) uses the formula 2.205 Lbs. = 1 Kg. 
                                || When the vehicle record is saved, the value entered in the "Front Tires PSI" field is saved in VTS2 in both metric and imperial and is used to populate the labels “Front Tires (kPa)”, “Front Tires (PSI)”. If the "Front Tires PSI" value is entered with the metric unit of measure (kPa), the conversion to imperial (PSI) uses the formula 1 kPa = 0.145 PSI and if the "Front Tires PSI" value is entered with the imperial unit of measure (PSI), the conversion to metric (kPa) uses the formula 1 PSI = 6.895 kPa. 
                                || When the vehicle record is saved, the value entered in the "Rear Tires PSI" field is saved in VTS2 in both metric and imperial and is used to populate the labels “Rear Tires (kPa)”, “Rear Tires (PSI)”. If the "Rear Tires PSI" value is entered with the metric unit of measure (kPa), the conversion to imperial (PSI) uses the formula 1 kPa = 0.145 PSI and if the "Rear Tires PSI" value is entered with the imperial unit of measure (PSI), the conversion to metric (kPa) uses the formula 1 PSI = 6.895 kPa. 
                                || When the vehicle record is saved, the “Number of Rear Tires” dropdown will drive the label “Rear Tire Config”. If the “Number of Rear Tires" dropdown value is 2, the “Rear Tire Config” will be “SINGLE”. If the “Number of Rear Tires" dropdown value is 4, the “Rear Tire Config” will be “DUAL”. 
                                || If the vehicle has been locked, the SAVE button will be greyed out and there will be red bold text in the header section saying, “VEHICLE LOCKED”. 
                                || `}
                            </ExplanationAccordion>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton
                  disabled={initialValues.isLocked}
                  loading={isLoading || isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
